import { toast } from '@solinkcorp/solink-components';
import { CallhomeApi } from 'clients';
import { AppDispatcher } from 'dispatcher';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import {
  ACCEPT_EULA,
  APPEND_USERS,
  CLEAR_CURRENT_USER,
  FAST_NOTIFICATION_DURATION,
  FETCH_CURRENT_USER_ERROR,
  FETCH_CURRENT_USER_SUCCESS,
  GET_DIGEST_SETTINGS,
  GET_REAL_TIME_SETTINGS,
  GET_USERS_ERROR,
  RECV_USERS,
  UPDATE_CURRENT_USER_ERROR,
  UPDATE_CURRENT_USER_SUCCESS,
  UPDATE_DIGEST_SETTINGS,
  UPDATE_DIGEST_SETTINGS_ERROR,
  UPDATE_MFA,
  UPDATE_REAL_TIME_SETTINGS_ERROR,
  UPDATE_REAL_TIME_SETTINGS_SUCCESS,
  USERS_REQUEST_LIMIT,
  USER_ADDED,
  USER_ADDED_ERROR,
  USER_REMOVED,
  USER_REMOVED_ERROR,
  USER_ROLE_CAMERA_RESTRICTIONS,
} from 'vd-constants';

// The users list doesn't contain guests by default
const getAllUsers = (tenantId, skip = 0, withGuests = false) => {
  const requestLimit = USERS_REQUEST_LIMIT;

  CallhomeApi()
    .getCustomerUsers(tenantId, requestLimit, skip, withGuests)
    .then((users) => {
      const action = skip === 0 ? RECV_USERS : APPEND_USERS;

      AppDispatcher.dispatch(action, {
        payload: users,
      });

      if (users.length === requestLimit) {
        getAllUsers(tenantId, skip + users.length);
      }
    })
    .catch((err) => {
      console.error('Unable to retrieve users', err);
      AppDispatcher.dispatch(GET_USERS_ERROR);
    });
};

const createRealTimeNotification = ({ api, smsNumber }) => {
  return api
    .createSmsBinding({ smsNumber })
    .then((response) => {
      AppDispatcher.dispatch(UPDATE_REAL_TIME_SETTINGS_SUCCESS, {
        payload: response,
      });
    })
    .catch((err) => {
      console.error(err);
      AppDispatcher.dispatch(UPDATE_REAL_TIME_SETTINGS_ERROR, {});
    });
};

export default {
  getUsers() {
    const tenantId = AuthService.session.tenantId;
    getAllUsers(tenantId, false);
  },

  getUser(userId) {
    const authUserId = AuthService.session.userId;

    if (!userId) {
      userId = authUserId;
    }

    CallhomeApi()
      .getUser({ userId })
      .then((response) => {
        AppDispatcher.dispatch(FETCH_CURRENT_USER_SUCCESS, {
          payload: response,
        });
      })
      .catch((err) => {
        console.error(err);
        AppDispatcher.dispatch(FETCH_CURRENT_USER_ERROR, {});
      });
  },

  updateUser({
    userId,
    data,
    callback = () => {},
    errorCallback = () => {},
    showNotification = true,
  }) {
    CallhomeApi()
      .updateUser(userId, data)
      .then((response) => {
        AppDispatcher.dispatch(UPDATE_CURRENT_USER_SUCCESS, {
          payload: response,
          showNotification,
        });
        callback();
      })
      .catch((err) => {
        AppDispatcher.dispatch(UPDATE_CURRENT_USER_ERROR, {});
        errorCallback();
        console.error(err);
      });
  },

  clearCurrentUser() {
    AppDispatcher.dispatch(CLEAR_CURRENT_USER);
  },

  addUser(user, callback) {
    const { name, givenName, familyName, email, type, phoneNumber } = user;
    const userName = name || `${givenName} ${familyName}`;

    AnalyticsLogger.addUser({
      email,
      name: userName,
      userType: type,
    });

    // Map the user object to something that Callhome can consume
    const mapped = {
      email,
      userType: type,
      name: userName,
      customerId: AuthService.session.tenantId,
      ...(phoneNumber ? { phoneNumber } : {}),
    };

    CallhomeApi()
      .addUser(mapped)
      .then((data) => {
        AppDispatcher.dispatch(USER_ADDED, {
          data,
        });
        callback(data);
      })
      .catch((error) => {
        console.error('Error adding user', error);
        AppDispatcher.dispatch(USER_ADDED_ERROR, {
          data: error,
        });
      });
  },

  getDigestSettings() {
    const userId = AuthService.session.userId;

    CallhomeApi()
      .getDigestSettings({ userId })
      .then((response) => {
        AppDispatcher.dispatch(GET_DIGEST_SETTINGS, {
          payload: response,
        });
      })
      .catch((err) => {
        console.error('Error retrieving digest settings', err);
      });
  },

  updateDigestSettings(settings) {
    const userId = AuthService.session.userId;

    CallhomeApi()
      .updateDigestSettings({ userId, settings })
      .then((response) => {
        AppDispatcher.dispatch(UPDATE_DIGEST_SETTINGS, {
          payload: response.digestSettings,
        });
      })
      .catch((err) => {
        console.error('Error updating digest settings', err);
        AppDispatcher.dispatch(UPDATE_DIGEST_SETTINGS_ERROR, {
          payload: err,
        });
      });
  },

  deleteUser({ id, email, successCallback, errorCallback }) {
    AnalyticsLogger.deleteUser({ email });

    CallhomeApi()
      .deleteUser(id)
      .then(() => {
        AppDispatcher.dispatch(USER_REMOVED, {
          id,
        });

        if (successCallback) {
          successCallback();
        }
      })
      .catch((err) => {
        console.error('Error deleting user', err);
        AppDispatcher.dispatch(USER_REMOVED_ERROR);

        if (errorCallback) {
          errorCallback();
        }
      });
  },

  getCameraRestrictions() {
    const userRoleId = AuthService.session.userRoleId;

    if (userRoleId) {
      CallhomeApi()
        .getCameraRestrictions(userRoleId)
        .then((response) => {
          AppDispatcher.dispatch(USER_ROLE_CAMERA_RESTRICTIONS, {
            cameras: response.cameras,
          });
        })
        .catch((err) => {
          console.error(err);
        });
    }
  },

  getRealTimeNotificationBindings() {
    const userId = AuthService.session.userId;

    CallhomeApi()
      .getRealTimeNotificationBindings({ userId })
      .then((response) => {
        AppDispatcher.dispatch(GET_REAL_TIME_SETTINGS, {
          payload: response,
        });
      })
      .catch((err) => {
        console.error(err);
      });
  },

  deleteRealTimeNotificationBinding({ smsId }) {
    if (smsId) {
      const userId = AuthService.session.userId;

      CallhomeApi()
        .deleteRealTimeNotificationBinding({ userId, bindingId: smsId })
        .then(() => {
          AppDispatcher.dispatch(UPDATE_REAL_TIME_SETTINGS_SUCCESS, {});
        })
        .catch((err) => {
          console.error(err);
          AppDispatcher.dispatch(UPDATE_REAL_TIME_SETTINGS_ERROR, {});
        });
    }
  },

  createRealTimeNotificationBinding({ smsNumber, smsId }) {
    const userId = AuthService.session.userId;
    const api = CallhomeApi();

    if (smsId) {
      // Delete existing SMS notification binding before creating new one
      return api
        .deleteRealTimeNotificationBinding({ userId, bindingId: smsId })
        .then(() => {
          return createRealTimeNotification({ api, smsNumber });
        })
        .catch((err) => {
          console.error(err);
          AppDispatcher.dispatch(UPDATE_REAL_TIME_SETTINGS_ERROR, {});
        });
    }
    return createRealTimeNotification({ api, smsNumber });
  },

  acceptEula({ locale }) {
    CallhomeApi()
      .acceptEula()
      .then((response) => {
        toast.success(locale.alarms.acceptedTheTerms, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        AppDispatcher.dispatch(ACCEPT_EULA, { payload: response });
      })
      .catch((err) => {
        console.error(err);
        toast.error(locale.error.problemAcceptingEula, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
      });
  },

  updateMfa({
    userId,
    data,
    callback = () => {},
    errorCallback = () => {},
    showNotification = true,
  }) {
    const { isMfaEnabled } = data;
    const mfaAction = isMfaEnabled ? 'enableMfa' : 'disableMfa';

    CallhomeApi()
      [mfaAction]({ userId })
      .then((response) => {
        AppDispatcher.dispatch(UPDATE_MFA, {
          payload: response,
          showNotification,
        });
        callback(response);
      })
      .catch((err) => {
        AppDispatcher.dispatch(UPDATE_MFA, {});
        errorCallback();
        console.error(err);
      });
  },
};
